import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gauge, Fuel, Users, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Models = () => {
  const { language } = useLanguage();
  const t = translations[language].models;
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = [
    { key: 'All', label: t.all },
    { key: 'Off-road', label: t.offRoad },
    { key: 'SUV', label: t.suv },
    { key: 'Sedan', label: t.sedan }
  ];

  const allModels = t.list;

  const filteredModels =
    selectedCategory === 'All'
      ? allModels
      : allModels.filter((model) => model.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white pt-[120px]">
      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center text-center">
          <div>
            <h1 className="text-6xl font-bold text-white mb-4">{t.title}</h1>
            <p className="text-xl text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${selectedCategory === category.key
                ? 'bg-red-600 text-white shadow-lg scale-105'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Models Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredModels.map((model, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full font-semibold text-sm">
                  {categories.find(c => c.key === model.category)?.label || model.category}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{model.name}</h3>
                <p className="text-red-600 font-semibold text-lg mb-4">{model.price}</p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Gauge className="h-4 w-4" />
                    <span className="text-sm">{model.specs.power}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Fuel className="h-4 w-4" />
                    <span className="text-sm">{model.specs.fuel}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">{model.specs.seats}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Settings className="h-4 w-4" />
                    <span className="text-sm">{model.specs.drive}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                    {t.viewDetails}
                  </Button>
                  <Link to="/test-drive" className="flex-1">
                    <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-50">
                      {t.testDrive}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Models;